import { BellOutlined } from "@ant-design/icons";
import { Empty, Popover, Skeleton } from "antd";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { LangContext, UserDataContext } from "../../contexts/Contexts";
import { getNotificationsService } from "../../../infrastructure/services/NotificationsService";
import { Notification } from "./Notification";
import "../../styles/notifications.css";

const notificationsMenuCopy = {
  en: {
    title: "Notifications",
    unread: "Unread",
    read: "Read",
    empty: "You have no new notifications",
    showLess: "Show fewer notifications",
    viewAll: "View all notifications",
  },
  es: {
    title: "Notificaciones",
    unread: "No leídas",
    read: "Leídas",
    empty: "No tienes notificaciones nuevas",
    showLess: "Mostrar menos notificaciones",
    viewAll: "Ver todas las notificaciones",
  },
};

const notificationsPageSize = 50;

export const NotificationsMenu = ({ notifications }) => {
  const maxNotificationsToShow = 5;
  const [showAll, setShowAll] = useState(false);
  const [activeTab, setActiveTab] = useState("unread");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [readNotifications, setReadNotifications] = useState([]);
  const loadedReadUserRef = useRef(null);
  const { userData } = useContext(UserDataContext);
  const { lang } = useContext(LangContext);

  const copy = useMemo(
    () =>
      notificationsMenuCopy[
        lang?.toLowerCase?.().startsWith("es") ? "es" : "en"
      ],
    [lang],
  );

  const isUnreadNotification = (notification) => notification?.seen === false;
  const formatCounter = (count) => (count > 99 ? "99+" : count);

  const unreadNotifications = useMemo(
    () => (notifications || []).filter(isUnreadNotification),
    [notifications],
  );

  const fetchReadNotifications = async ({ force = false } = {}) => {
    const userId = userData?.idUsuario;
    if (!userId) {
      setReadNotifications([]);
      loadedReadUserRef.current = null;
      return [];
    }

    if (!force && loadedReadUserRef.current === String(userId)) {
      return readNotifications;
    }

    setLoading(true);
    try {
      const nextReadNotifications = await getNotificationsService(userId, {
        page: 1,
        pageSize: notificationsPageSize,
        visto: true,
        forceRefresh: force,
      });
      setReadNotifications(nextReadNotifications || []);
      loadedReadUserRef.current = String(userId);
      return nextReadNotifications || [];
    } catch (error) {
      console.error("Failed loading read notifications", error);
      setReadNotifications([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadedReadUserRef.current = null;
    setReadNotifications([]);
  }, [userData?.idUsuario]);

  useEffect(() => {
    if (!open || activeTab !== "read" || !userData?.idUsuario) {
      return;
    }

    fetchReadNotifications();
    // fetchReadNotifications intentionally depends on the current user/tab state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, open, userData?.idUsuario]);

  useEffect(() => {
    if (!open || !userData?.idUsuario) {
      return;
    }

    const handleTabRefresh = () => {
      if (activeTab === "read") {
        fetchReadNotifications({ force: true });
      }
      // La pestaña no leída se actualiza desde NotificationsFeedContext.
    };

    window.addEventListener("notifications:tab-refresh", handleTabRefresh);

    return () => {
      window.removeEventListener("notifications:tab-refresh", handleTabRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, open, userData?.idUsuario]);

  const tabNotifications =
    activeTab === "read" ? readNotifications : unreadNotifications;

  const notificationsContent = (
    <div className="notifications-pop notifications-panel">
      <div className="notifications-panel__header">
        <h3>{copy.title}</h3>
      </div>

      <div className="notifications-panel__tabs">
        <button
          type="button"
          className={`notifications-panel__tab ${
            activeTab === "unread" ? "notifications-panel__tab--active" : ""
          }`}
          onClick={() => setActiveTab("unread")}
        >
          <span>{copy.unread}</span>
          <span className="notifications-panel__tab-count">
            {formatCounter(unreadNotifications.length)}
          </span>
        </button>
        <button
          type="button"
          className={`notifications-panel__tab ${
            activeTab === "read" ? "notifications-panel__tab--active" : ""
          }`}
          onClick={() => setActiveTab("read")}
        >
          <span>{copy.read}</span>
          <span className="notifications-panel__tab-count">
            {formatCounter(readNotifications.length)}
          </span>
        </button>
      </div>

      <div className="notifications-panel__list">
        {loading ? (
          <div className="notifications-panel__empty">
            <Skeleton active avatar paragraph={{ rows: 2 }} />
            <Skeleton active avatar paragraph={{ rows: 2 }} />
          </div>
        ) : tabNotifications.length ? (
          tabNotifications
            .slice(
              0,
              showAll ? tabNotifications.length : maxNotificationsToShow,
            )
            .map((notification, index) => (
              <div key={notification.notificationId ?? index}>
                <Notification notification={notification} />
              </div>
            ))
        ) : (
          <div className="notifications-panel__empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={copy.empty}
            />
          </div>
        )}
      </div>

      {tabNotifications.length > maxNotificationsToShow ? (
        <button
          type="button"
          className="notifications-panel__footer-link"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? copy.showLess : copy.viewAll}
        </button>
      ) : null}
    </div>
  );

  return (
    <div>
      <Popover
        content={notificationsContent}
        trigger={"click"}
        open={open}
        onOpenChange={setOpen}
        destroyTooltipOnHide={true}
        zIndex={10060}
        overlayClassName="notifications-popover-overlay"
        getPopupContainer={() => document.body}
      >
        <button
          type="button"
          className="notifications-action-button"
          aria-label={copy.title}
        >
          <BellOutlined className="notifications-action-icon" />
        </button>
      </Popover>
    </div>
  );
};
